"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, Tooltip as RechartsTooltip } from "recharts";
import { Search, TrendingUp, Target, MapPin, ArrowRight, ShieldCheck, ChartBar as BarChart3, CircleCheck as CheckCircle2, Sparkles, Building2, Compass, Layers, FileText } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const searchTrendData = [{
  sector: "B2B Services",
  volume: 14200,
  growth: "+38%",
  gapScore: 78
}, {
  sector: "Professional & Legal",
  volume: 18600,
  growth: "+42%",
  gapScore: 84
}, {
  sector: "Commercial Trades",
  volume: 22400,
  growth: "+29%",
  gapScore: 65
}, {
  sector: "Suffolk Logistics",
  volume: 11800,
  growth: "+51%",
  gapScore: 91
}, {
  sector: "E-Commerce & Retail",
  volume: 29500,
  growth: "+33%",
  gapScore: 72
}];
const keywordOpportunities = [{
  keyword: "seo consultant ipswich",
  intent: "High Commercial",
  volume: "880/mo",
  difficulty: "Medium",
  opportunity: "High conversion rate for local businesses looking for independent expertise rather than generic agencies."
}, {
  keyword: "b2b marketing consultancy suffolk",
  intent: "Transactional",
  volume: "1,200/mo",
  difficulty: "Low-Medium",
  opportunity: "Significant gap in technical B2B positioning and structured schema coverage across East Anglia."
}, {
  keyword: "commercial solicitor ipswich",
  intent: "High Intent",
  volume: "2,400/mo",
  difficulty: "High",
  opportunity: "Local map pack dominance opportunity through structured local landing pages and high-authority links."
}, {
  keyword: "logistics company felixstowe ipswich",
  intent: "Commercial",
  volume: "3,100/mo",
  difficulty: "Medium",
  opportunity: "Suffolk supply chain keywords have massive search intent with fragmented organic competition."
}];
const neighboringLocations = [{
  name: "Norwich",
  path: "/LocalSeoConsultantNorwichPage",
  distance: "43 miles",
  focus: "B2B & Tech Services"
}, {
  name: "Peterborough",
  path: "/LocalSeoConsultantPeterboroughPage",
  distance: "78 miles",
  focus: "Industrial & Logistics"
}, {
  name: "Northampton",
  path: "/LocalSeoConsultantNorthamptonPage",
  distance: "85 miles",
  focus: "Commercial & Distribution"
}, {
  name: "Milton Keynes",
  path: "/LocalSeoConsultantMiltonKeynesPage",
  distance: "92 miles",
  focus: "Corporate & Enterprise"
}, {
  name: "Lincoln",
  path: "/LocalSeoConsultantLincolnPage",
  distance: "115 miles",
  focus: "Regional Professional Services"
}, {
  name: "Doncaster",
  path: "/LocalSeoConsultantDoncasterPage",
  distance: "140 miles",
  focus: "E-Commerce & Supply Chain"
}];
const strategicPillars = [{
  id: "trends",
  title: "Ipswich Search Trends",
  icon: TrendingUp,
  summary: "East Anglia businesses are rapidly shifting to digital-first search discovery.",
  details: "My deep analysis of Ipswich and Suffolk search patterns reveals a 36% year-on-year increase in commercial search intent. Local decision-makers now research B2B suppliers, legal firms, and specialized services through Google long-tail queries before ever reaching out."
}, {
  id: "competition",
  title: "Competitor Vulnerability",
  icon: Target,
  summary: "Legacy agencies in East Anglia rely on outdated, surface-level SEO tactics.",
  details: "Most Ipswich websites suffer from thin content, unoptimized Google Business Profiles, slow page loading, and missing schema markup. When you work directly with me, we exploit these exact technical gaps to outrank entrenched competitors."
}, {
  id: "opportunities",
  title: "Hyper-Local Opportunities",
  icon: Sparkles,
  summary: "Targeted Suffolk intent offers high-converting traffic with lower acquisition costs.",
  details: "By engineering bespoke local content hubs, optimizing for Ipswich geofenced searches, and structuring your site for AI Search Overview (SGE) inclusion, I turn regional search queries into qualified inbound leads."
}];
export default function Ipswichseo() {
  const [activeTab, setActiveTab] = useState("trends");
  const shouldReduceMotion = useReducedMotion();
  const selectedPillar = strategicPillars.find(p => p.id === activeTab) || strategicPillars[0];
  return <section id="ipswichseoanalysis" data-nav="light" className="relative w-full bg-background py-16 md:py-24 text-foreground overflow-hidden">
      <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-secondary/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 w-96 h-96 bg-muted/60 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5">
            <Compass className="w-3.5 h-3.5 text-primary" />
            Regional Search Intelligence
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Ipswich SEO Search Trends &amp; Market Opportunities
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            I analyze local Suffolk search behaviors, technical competitor gaps, and conversion opportunities so your business can systematically capture valuable Ipswich search traffic. You work directly with me — Yassine Essaadi — as your personal SEO strategy partner.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <Card className="bg-card border-border shadow-xs hover:border-primary/40 transition-[border-color]">
            <CardHeader className="p-5 pb-2">
              <CardDescription className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center justify-between">
                <span>Suffolk Search Volume</span>
                <Search className="w-4 h-4 text-primary" />
              </CardDescription>
              <CardTitle className="text-2xl font-bold text-foreground">96,500+</CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0">
              <p className="text-xs text-muted-foreground">Monthly commercial queries across Ipswich &amp; Greater Suffolk.</p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border shadow-xs hover:border-primary/40 transition-[border-color]">
            <CardHeader className="p-5 pb-2">
              <CardDescription className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center justify-between">
                <span>Avg. Competitor Gap</span>
                <BarChart3 className="w-4 h-4 text-accent" />
              </CardDescription>
              <CardTitle className="text-2xl font-bold text-foreground">78 / 100</CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0">
              <p className="text-xs text-muted-foreground">High gap score indicating uncaptured organic market share.</p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border shadow-xs hover:border-primary/40 transition-[border-color]">
            <CardHeader className="p-5 pb-2">
              <CardDescription className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center justify-between">
                <span>Yearly Intent Growth</span>
                <TrendingUp className="w-4 h-4 text-chart-4" />
              </CardDescription>
              <CardTitle className="text-2xl font-bold text-foreground">+36%</CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0">
              <p className="text-xs text-muted-foreground">Rising online commercial intent among Ipswich buyers.</p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border shadow-xs hover:border-primary/40 transition-[border-color]">
            <CardHeader className="p-5 pb-2">
              <CardDescription className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center justify-between">
                <span>Direct Access</span>
                <ShieldCheck className="w-4 h-4 text-primary" />
              </CardDescription>
              <CardTitle className="text-2xl font-bold text-foreground">100% Personal</CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0">
              <p className="text-xs text-muted-foreground">No account managers. Every audit &amp; strategy handled by me.</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          <div className="lg:col-span-5 space-y-4">
            <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Strategic Breakdown
            </h3>
            <p className="text-sm text-muted-foreground">
              Select an area below to inspect how I evaluate Ipswich market positioning and create high-converting SEO campaigns.
            </p>

            <div className="space-y-3 pt-2">
              {strategicPillars.map((pillar, i) => {
              const IconComponent = pillar.icon;
              const isActive = activeTab === pillar.id;
              return <div key={pillar.id} data-index={i}>
                    <Button variant={isActive ? "default" : "outline"} onClick={() => setActiveTab(pillar.id)} className={`w-full justify-start text-left h-auto py-3.5 px-4 transition-all ${isActive ? "bg-primary text-primary-foreground shadow-md" : "bg-card hover:bg-muted text-foreground border-border"}`}>
                      <div className="flex items-start gap-3 w-full">
                        <div className={`p-2 rounded-md shrink-0 ${isActive ? "bg-primary-foreground/15 text-primary-foreground" : "bg-muted text-primary"}`}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm">{pillar.title}</div>
                          <div className={`text-xs line-clamp-1 mt-0.5 ${isActive ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                            {pillar.summary}
                          </div>
                        </div>
                      </div>
                    </Button>
                  </div>;
            })}
            </div>

            <Card className="bg-card border-border mt-6 shadow-xs">
              <CardHeader className="p-5 pb-3">
                <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  {selectedPillar.title} Focus
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5 pt-0 space-y-3">
                <p className="text-sm text-muted-foreground leading-relaxed">{selectedPillar.details}</p>
                <div className="pt-2 flex items-center gap-2 text-xs font-medium text-primary">
                  <CheckCircle2 className="w-4 h-4" />
                  Direct consultant oversight on every deliverable
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-7">
            <Card className="bg-card border-border shadow-xs h-full flex flex-col justify-between">
              <CardHeader className="p-6 pb-2">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-lg font-semibold text-foreground">
                      Ipswich Industry Search Intent Distribution
                    </CardTitle>
                    <CardDescription className="text-xs text-muted-foreground mt-1">
                      Monthly search queries and vulnerability score across major Suffolk sectors
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="w-fit text-xs border-primary/30 text-primary">
                    Suffolk Market Data
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="p-6 pt-4">
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={searchTrendData} margin={{
                    top: 15,
                    right: 10,
                    left: -15,
                    bottom: 25
                  }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
                      <XAxis dataKey="sector" tick={{
                      fill: "var(--color-muted-foreground)",
                      fontSize: 13
                    }} interval={0} angle={-15} textAnchor="end" />
                      <YAxis tick={{
                      fill: "var(--color-muted-foreground)",
                      fontSize: 13
                    }} />
                      <RechartsTooltip contentStyle={{
                      backgroundColor: "var(--color-card)",
                      borderColor: "var(--color-border)",
                      borderRadius: "0.5rem",
                      color: "var(--color-foreground)",
                      fontSize: "14px"
                    }} />
                      <Bar dataKey="volume" radius={[4, 4, 0, 0]}>
                        {searchTrendData.map((entry, index) => <Cell key={`cell-${index}`} fill={index % 2 === 0 ? "var(--color-primary)" : "var(--color-accent)"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <Separator className="my-4" />

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <span className="text-xs text-muted-foreground block">Top Sector</span>
                    <span className="text-sm font-semibold text-foreground">E-Commerce &amp; Retail</span>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <span className="text-xs text-muted-foreground block">Fastest Growing</span>
                    <span className="text-sm font-semibold text-foreground">Suffolk Logistics (+51%)</span>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg col-span-2 sm:col-span-1">
                    <span className="text-xs text-muted-foreground block">Highest Gap</span>
                    <span className="text-sm font-semibold text-foreground">Logistics (91/100)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="space-y-6 mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <h3 className="text-2xl font-semibold text-foreground flex items-center gap-2">
                <Layers className="w-5 h-5 text-primary" />
                Ipswich High-Intent Keyword Opportunities
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Examples of commercial search terms with strong buyer intent in Ipswich and Suffolk
              </p>
            </div>
            <Link to="/ContactPage">
              <Button variant="outline" size="sm" className="gap-2 self-start md:self-auto">
                Request Full Keyword Audit
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {keywordOpportunities.map((item, i) => <Card key={item.keyword} data-index={i} className="bg-card border-border hover:border-primary/40 transition-[border-color] shadow-xs">
                <CardHeader className="p-5 pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="text-xs font-mono font-medium text-primary uppercase tracking-wider block mb-1">
                        {item.intent}
                      </span>
                      <CardTitle className="text-base font-semibold text-foreground font-mono">
                        "{item.keyword}"
                      </CardTitle>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {item.volume}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-5 pt-0 space-y-3">
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.opportunity}</p>
                  <div className="flex items-center justify-between text-xs pt-1 border-t border-border/50">
                    <span className="text-muted-foreground">SEO Difficulty:</span>
                    <span className="font-medium text-foreground">{item.difficulty}</span>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>

        <div className="bg-muted/40 border border-border rounded-xl p-6 md:p-8 space-y-6 mb-16">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Neighboring East Anglia &amp; Regional Hubs
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Explore local SEO strategies for nearby commercial centers across the East of England and Midlands
              </p>
            </div>
            <Badge variant="outline" className="w-fit text-xs bg-background">
              Regional Coverage
            </Badge>
          </div>

          <Separator />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {neighboringLocations.map((loc, i) => <div key={loc.name} data-index={i}>
                <Link to={loc.path} className="block group">
                  <div className="p-4 rounded-lg bg-card border border-border group-hover:border-primary/50 group-hover:shadow-xs transition-all">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-foreground group-hover:text-primary transition-colors flex items-center gap-1.5 text-sm">
                        <Building2 className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        SEO Consultant {loc.name}
                      </span>
                      <span className="text-xs text-muted-foreground">{loc.distance}</span>
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center justify-between">
                      <span>Focus: {loc.focus}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:translate-x-1 group-hover:text-primary transition-all" />
                    </p>
                  </div>
                </Link>
              </div>)}
          </div>
        </div>

        <div className="bg-primary text-primary-foreground rounded-2xl p-8 md:p-12 text-center space-y-6 shadow-md relative overflow-hidden">
          <div className="max-w-2xl mx-auto space-y-3">
            <h3 className="text-xl sm:text-2xl font-semibold tracking-tight">
              Ready to Outrank Your Ipswich Competitors?
            </h3>
            <p className="text-sm sm:text-base text-primary-foreground/85 leading-relaxed">
              Skip agency middlemen. Book a free, direct SEO consultation with me to review your website's Ipswich search performance and uncover actionable organic growth opportunities.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Link to="/ContactPage">
              <Button size="lg" className="bg-background text-foreground hover:bg-background/90 font-medium px-6">
                Book a Free SEO Consultation
              </Button>
            </Link>
            <Link to="/CaseStudiesPage">
              <Button variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 px-6">
                Explore My Case Studies
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}